import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import {
    Button,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    TableContainer,
    Tabs,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';

import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import config from '../config';
import SelectedGameContext from '../context/SelectedGameContext';
import InventoryDataCaller from '../util/inventoryDataCaller';

import * as INVMANAGEMENT from '../consts/InventoryManagement';
import EditItem from '../forms/EditItem';
import EditProductCost from '../forms/EditProductCost';
import { ProductCost, ProductDetail } from '../interfaces/IInventory';

interface InventoryItem {
    item_id: string;
    item_name: string;
    item_type: string;
    item_desc: string;
    quantity: number;
}

interface UserInventoryItem {
    item_id: string;
    item_name: string;
    item_type: string;
    item_desc: string;
    quantity: number;
}

interface FullInventoryItem {
    item_id: string;
    item_name: string;
    item_type: string;
    item_desc: string;
}

// NOTE: Bingo needed the functionality for editing inventory items asap, and since there was only one bingo item owner, Ryan's prod ID, we went with hardcoding it to get the full list of items for bingo
//      The same is now being done for WOF owner ID as well since it would be nice for them to be able to edit inventory items as well.
// TODO: Rewrite the call to get all inventory items so that it takes into account all owner IDs for a license (will require a call to get all owner IDs first)
const BINGO_OWNER_ID = '6196834d-3c86-4bfb-b536-bf1ee821194b'; // Ryan's Prod ID
const WOF_OWNER_ID = 'de2861f3-c428-47ac-8288-61c54bf359b1'; // Shu's Prod ID

const BINGO_LICENSE = "d2eea033-f28d-41c6-a992-f85a55c70ab2";
const WOF_LICENSE = "65283b86-dfc9-4631-86ec-f6940d6b5ffc";

export const InventoryManagement: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const toast = useToast();
    const [userInventory, setUserInventory] = useState<Array<{ [key: string]: any }>>();
    const [newInventory, setNewInventory] = useState<Array<{ [key: string]: any }>>();
    const [fullProductInventory, setFullProductInventory] = useState<Array<{ [key: string]: any }>>();
    const [inventoryLoaded, setInventoryLoaded] = useState(false);
    const [products, setProducts] = useState<Array<{ [key: string]: any }>>([]);
    const [productsLoaded, setProductsLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [userID, setUserID] = useState('');
    const [gettingUserInfo, setGettingUserInfo] = useState(false);
    const [multipleUsers, setMultipleUsers] = useState<Array<{ [key: string]: any }>>([]);
    const { keycloak } = useKeycloak();

    // hooks for editing items
    const {
        isOpen: isEditItemOpen,
        onOpen: onEditItemOpen,
        onClose: onEditItemClose,
    } = useDisclosure();
    const [editingItemId, setEditingItemID] = useState<string | undefined>();

    // hooks for editing product costs
    const {
        isOpen: isEditProductCostOpen,
        onOpen: onEditProductCostOpen,
        onClose: onEditProductCostClose,
    } = useDisclosure();
    const [editingProductCostId, setEditingProductCostID] = useState<string | undefined>();

    const url = `${config.inventoryServiceURL}`;

    // dataCaller util to handle data requests

    // TODO: Move to separate util function
    const getOwners = async () => {
        try {
            const response = await fetch(
                `${config.licenseServiceURL}/license/${currLicense.key}/owners`,
                {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`,
                    },
                },
            );
            if (response.status === 200) {
                const data = await response.json();
                const ownersArray = data.data;
                return ownersArray;
            } else {
                return toast({
                    title: 'Unable to get information',
                    description: 'Unable to display list of license owners',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }
        } catch (error) {
            console.log(error);
            return;
        }
    };

    const getFullProductInventory = async (itemOwnerId: string) => {
        try {
            const response = await fetch(`${url}/item?item_owner=${itemOwnerId}`, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`,
                },
            });
            if (response.status === 200) {
                const data = await response.json();
                const fullInventoryList = data.data;
                setFullProductInventory(fullInventoryList);
                return fullInventoryList;
            } else {
                return toast({
                    title: 'Unable to get inventory',
                    description: 'Unable to display full inventory list',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }
            // return response;
        } catch (error) {
            console.log(error);
            return;
        }
    };

    const createUpdatedInventory = (
        fullInventory: { [key: string]: any }[],
        userInventory: { [key: string]: any }[],
    ): any => {
        const updatedInventory: { [key: string]: any }[] = fullInventory.map((item) => ({
            ...item,
            quantity: 0, // Default quantity is set to 0
        }));

        userInventory.forEach((userItem) => {
            const matchingItem = updatedInventory.find(
                (item) => item.item_id === userItem.inventory_item.item_id,
            );
            if (matchingItem) {
                matchingItem.quantity = userItem.quantity;
            }
        });

        return updatedInventory;
    };

    const getUserInfo = async (username: string) => {
        setUserID('');
        if (searchTerm !== '') {
            setGettingUserInfo(true);
            const resp = await fetch(
                `${config.socialServiceURL}/users/profiles/public?username=${username}`,
                {
                    headers: {
                        Authorization: `Bearer ${keycloak.token!}`,
                    },
                },
            );
            if (resp.status !== 200) {
                setUserID('');
                setGettingUserInfo(false);
                return toast({
                    title: 'Unable to get information',
                    description: 'Unable to get user information',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }

            const respData = await resp.json();
            if (!respData.data) {
                setUserID('');
                setGettingUserInfo(false);
                return toast({
                    title: 'Unable to get information',
                    description: 'Unable to get user information',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }

            setMultipleUsers(respData.data);
            if (respData.data.length > 1) {
                return toast({
                    title: 'Multiple users found',
                    description: 'Please select the user you desire most...',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
            } else if (respData.data.length === 1) {
                setUserID(respData.data[0].id);
                setGettingUserInfo(false);
                return toast({
                    title: 'Found',
                    description: `Found ${username}!`,
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setUserID('');
                setGettingUserInfo(false);
                return toast({
                    title: 'No User Found',
                    description: 'No user was found. Try a differnet username?',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } else {
            return toast({
                title: 'Search Bar Empty',
                description: 'Please enter a username or email to search',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const handleEnterSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getUserInfo(searchTerm);
        }
    };

    const handleEditProductCostSuccess = (changedProduct: ProductDetail, costs: ProductCost[]) => {
        setProducts(products => products.map(product => {
            if (product.product_id === changedProduct.product_id) {
                return {
                    ...product,
                    cost: costs,
                }
            }
            return product;
        }));
    };

    const renderProductCost = (costs: { [key: string]: any }[]) => {
        if (!costs) {
            return <div>null</div>;
        }
        return costs.map((cost, index) => {
          let renderedCost = '';
      
          if (cost.currency) {
            switch (cost.currency.toLowerCase()) {
              case 'cad':
                renderedCost = `CAD $${cost.quantity}`;
                break;
              case 'usd':
                renderedCost = `USD $${cost.quantity}`;
                break;
              case 'eur':
                renderedCost = `EUR €${cost.quantity}`;
                break;
              default:
                renderedCost = `${cost.currency.toUpperCase()} ${cost.quantity}`;
            }
          } else {
            if (cost.cost_type) {
                renderedCost = `${cost.quantity} (${cost.cost_type})`;
            } else {
                renderedCost = `${cost.quantity}`;
            }
          }
      
          return <div key={index}>{renderedCost}</div>;
        });
      };

    const renderProducts = () => {
        if (!products || products.length === 0) {
            return (
                <Center>
                    {INVMANAGEMENT.INVENTORY_MANAGEMENT_NO_PRODUCTS}
                </Center>
            );
        }

        return (
            <>
                <TableContainer maxWidth={'100%'}>
                    <Table variant="striped" colorScheme="gray">
                        <Thead>
                            <Tr>
                                <Th>Product ID</Th>
                                <Th>Name</Th>
                                <Th>Type</Th>
                                <Th>Cost</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {products?.map((product) => (
                                <Tr key={product.product_id}>
                                    <Td>{product.product_id}</Td>
                                    <Td>{product.product_name}</Td>
                                    <Td>{product.product_type}</Td>
                                    <Td>{renderProductCost(product.cost)}</Td>
                                    <Td>
                                        <Button
                                            onClick={() => {
                                                setEditingProductCostID(product.product_id);
                                                onEditProductCostOpen();
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
                {editingProductCostId && isEditProductCostOpen && (
                    <EditProductCost
                        productId={editingProductCostId}
                        isOpen={isEditProductCostOpen}
                        onClose={onEditProductCostClose}
                        onSuccess={handleEditProductCostSuccess}
                    />
                )}
            </>
        );
    };

    const renderProductsTab = () => {
        return (
            <Box>
                <Heading>
                    {INVMANAGEMENT.INVENTORY_MANAGEMENT_PRODUCT_HEADING}
                </Heading>
                {/* <Box mt={3} mb={3} mr={3}>
                    <Heading as="h5" size={"md"}>
                        {INVMANAGEMENT.INVENTORY_MANAGEMENT_PRODUCT_SUBHEADING}
                    </Heading>
                </Box> */}
                <br />
                <Box>
                    <Flex justifyContent={"space-between"}>
                        <Heading as="h2" size="md">
                            {
                                INVMANAGEMENT.INVENTORY_MANAGEMENT_PRODUCT_DATA_TABLE_TITLE
                            }
                        </Heading>
                    </Flex>
                    {productsLoaded ? (
                        renderProducts()
                    ) : (
                        <Center width="100%" height="100%">
                            <Spinner data-cy="loading" />
                        </Center>
                    )}
                </Box>
            </Box>
        );
    };

    const renderInventory = () => {
        if (fullProductInventory?.length === 0 || gettingUserInfo) {
            return (
                <Center>
                    <Text>Inventory not found</Text>
                </Center>
            );
        }
        return (
            <Box maxWidth={'100%'}>
                {userID ? (
                    newInventory ? (
                        <>
                            <TableContainer maxWidth={'100%'}>
                                <Table variant="striped" colorScheme="gray">
                                    <Thead>
                                        <Tr>
                                            <Th>Item ID</Th>
                                            <Th>Name</Th>
                                            <Th>Type</Th>
                                            <Th>Quantity</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {newInventory?.map((inventoryItem) => (
                                            <Tr key={inventoryItem.item_id}>
                                                <Td>{inventoryItem.item_id}</Td>
                                                <Td>{inventoryItem.item_name}</Td>
                                                <Td>{inventoryItem.item_type}</Td>
                                                <Td>{inventoryItem.quantity}</Td>
                                                <Td>
                                                    <Button
                                                        onClick={() => {
                                                            setEditingItemID(inventoryItem.item_id);
                                                            onEditItemOpen();
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                            {editingItemId && isEditItemOpen && (
                                <EditItem
                                    userId={userID}
                                    itemId={editingItemId}
                                    isOpen={isEditItemOpen}
                                    onClose={onEditItemClose}
                                />
                            )}
                        </>
                    ) : (
                        <Center>
                            <Spinner></Spinner>
                        </Center>
                    )
                ) : (
                    <Center>
                        <Spinner></Spinner>
                    </Center>
                )}
            </Box>
        );
    };

    const replaceUserID = (newUserId: string, newUsername: string) => {
        setUserID(newUserId);
        setGettingUserInfo(false);
        setSearchTerm(newUsername);
        setMultipleUsers([]);
    };

    const getProducts = async () => {
        const dc = new InventoryDataCaller(url, keycloak.token!);
        const data = await dc.getAllProducts(currLicense.key);
        if (data.status !== 200) {
            setProductsLoaded(true);
            if (data.status === 404) {
                setProducts([]);

                return;
            }
            return;
        }

        const result = await data.json();

        setProducts(result.data);
        setProductsLoaded(true);
    };

    useEffect(() => {
        if (!currLicense) {
            return;
        }

        const getInventory = async () => {
            const dc = new InventoryDataCaller(url, keycloak.token!);
            const data = await dc.getInventory(userID);
            if (data.status !== 200) {
                setInventoryLoaded(true);
                if (data.status === 404) {
                    setUserInventory([]);
                }

                return toast({
                    title: 'Request Error',
                    description: `Failed to get inventory`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }

            const result = await data.json();

            setUserInventory(result.data);
            setInventoryLoaded(true);
        };

        getProducts();
        let itemOwnerId;
        switch (currLicense.key) {
            case WOF_LICENSE:
                itemOwnerId = WOF_OWNER_ID;
                break;
            case BINGO_LICENSE:
            default:
                itemOwnerId = BINGO_OWNER_ID;
        }
        getFullProductInventory(itemOwnerId);
        if (userID && userID !== '') {
            getInventory();
        }
    }, [currLicense, keycloak.token, url, userID, isEditItemOpen]);

    const renderInventoryTab = () => {
        return (
            <Box maxW={'100%'}>
                <Heading>{INVMANAGEMENT.INVENTORY_MANAGEMENT_INV_HEADING}</Heading>
                <Box mt={3} mb={3} mr={3}>
                    <Heading as="h5" size={'md'}>
                        {INVMANAGEMENT.INVENTORY_MANAGEMENT_INV_SUBHEADING}
                    </Heading>
                </Box>
                <Box>
                    <Flex justifyContent={'space-between'}>
                        <Heading as="h2" size="md" mb={3}>
                            {INVMANAGEMENT.INVENTORY_MANAGEMENT_INV_DATA_TABLE_TITLE}
                        </Heading>
                    </Flex>
                    <Box>
                        <InputGroup>
                            <Input
                                type="text"
                                placeholder="Search user..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleEnterSearch}
                                mb={4}
                                data-cy="search-input"
                            />
                            <InputRightElement>
                                <IconButton
                                    aria-label={'test'}
                                    icon={<FaSearch />}
                                    h="1.75rem"
                                    size="sm"
                                    onClick={() => getUserInfo(searchTerm)}
                                ></IconButton>
                            </InputRightElement>
                        </InputGroup>
                    </Box>
                    {multipleUsers?.length > 1 && (
                        <Menu defaultIsOpen={true} closeOnBlur={false}>
                            <MenuButton as={'p'}></MenuButton>
                            <MenuList>
                                {multipleUsers?.map((user) => (
                                    <MenuItem
                                        key={user.id}
                                        onClick={() => {
                                            replaceUserID(user.id, user.username);
                                        }}
                                    >
                                        {user.username}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    )}
                    {inventoryLoaded && !gettingUserInfo ? (
                        renderInventory()
                    ) : (
                        <Center width="100%" height="100%">
                            {gettingUserInfo && multipleUsers.length <= 1 && (
                                <Spinner data-cy="loading" />
                            )}
                        </Center>
                    )}
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        if (currLicense) {
            let itemOwnerId;
            switch (currLicense.key) {
                case WOF_LICENSE:
                    itemOwnerId = WOF_OWNER_ID;
                    break;
                case BINGO_LICENSE:
                default:
                    itemOwnerId = BINGO_OWNER_ID;
            }
            getFullProductInventory(itemOwnerId);
        }
    }, [currLicense]);

    useEffect(() => {
        if (fullProductInventory && userInventory) {
            setNewInventory(createUpdatedInventory(fullProductInventory, userInventory));
        }
    }, [fullProductInventory, userInventory]);

    return (
        <Tabs index={tabIndex} onChange={(index: number) => setTabIndex(index)} maxW={'100%'}>
            <TabList>
                <Tab>Products</Tab>
                <Tab>Inventory</Tab>
            </TabList>
            <TabPanels>
                <TabPanel>{renderProductsTab()}</TabPanel>
                <TabPanel>{renderInventoryTab()}</TabPanel>
            </TabPanels>
        </Tabs>
    );
};
